import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  ChevronLeft,
  Loader2,
  ShoppingBag,
  Store,
  Utensils,
  Zap,
} from 'lucide-react';
import GastifyBrand from './public/GastifyBrand';
import {
  BUSINESS_PRESET_LIST,
  completeFirstRunOnboarding,
  fetchOrganizationOnboardingStatus,
  finalizeFirstRunOnboarding,
  getBusinessPreset,
  shouldShowFirstRunOnboarding,
} from '../utils/onboarding';

const STEP_LABELS = [
  'Tipo de negocio',
  'Base operativa',
];

const PRESET_ICON_MAP = {
  comercio: Store,
  indumentaria: ShoppingBag,
  gastronomia: Utensils,
  servicios: Briefcase,
};

const buildFeatureLabel = (flags = {}) => {
  const items = [];
  if (flags.enable_variants) items.push('Variantes');
  if (flags.enable_cost_sync) items.push('Costos sincronizados');
  if (flags.enable_misc_product) items.push('Producto libre');
  if (flags.allow_negative_stock) items.push('Stock flexible');
  if (flags.allow_zero_sell_price) items.push('Precio $0 permitido');
  if (items.length === 0) items.push('Configuracion base');
  return items;
};

export default function FirstRunOnboarding({
  organizationId,
  userId,
  userRole,
  userEmail,
  onCompleted,
  onUnavailable,
}) {
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [fatalError, setFatalError] = useState('');
  const [step, setStep] = useState(0);
  const [selectedPresetKey, setSelectedPresetKey] = useState('comercio');
  const [saving, setSaving] = useState(false);
  const [entering, setEntering] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [result, setResult] = useState(null);

  // Refs para coordinar el auto-run sin meter saving/entering/result en deps del effect.
  // Si esos flags estuvieran en deps, el setSaving(true) inicial dispara un re-render
  // que cancela el cleanup del propio effect y aborta el auto-run a mitad de camino.
  const isMountedRef = useRef(true);
  const autoRunStartedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isAdminUser = userRole === 'admin' || userEmail?.toLowerCase() === 'adpenia@gmail.com';

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    let active = true;

    const loadOrganization = async () => {
      setLoading(true);
      setFatalError('');

      try {
        const data = await fetchOrganizationOnboardingStatus(organizationId);

        if (!active) return;

        setOrganization(data);
        setSelectedPresetKey(data?.business_type || 'comercio');
      } catch (error) {
        if (!active) return;
        console.error(error);
        setFatalError('No pudimos cargar la configuracion inicial de la empresa.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadOrganization();

    return () => {
      active = false;
    };
  }, [organizationId]);

  const selectedPreset = useMemo(
    () => getBusinessPreset(selectedPresetKey),
    [selectedPresetKey],
  );

  useEffect(() => {
    if (loading || fatalError || !organizationId || !organization) return undefined;
    if (autoRunStartedRef.current) return undefined;

    // Check if business_type is already selected (e.g. from step 2) but onboarding is not completed yet
    if (organization.business_type && !organization.onboarding_completed_at) {
      autoRunStartedRef.current = true;

      const autoRun = async () => {
        try {
          setSaving(true);
          setSaveError('');

          // 1. Run completeFirstRunOnboarding
          const nextResult = await completeFirstRunOnboarding({
            organizationId,
            actorUserId: userId,
            businessType: organization.business_type,
          });

          if (!isMountedRef.current) return;
          setResult(nextResult);
          setOrganization((current) => ({
            ...current,
            ...nextResult.organization,
          }));

          // 2. Run finalizeFirstRunOnboarding
          setEntering(true);
          const completionPatch = await finalizeFirstRunOnboarding({
            organizationId,
            actorUserId: userId,
          });

          if (!isMountedRef.current) return;
          const nextOrganization = {
            ...organization,
            ...nextResult.organization,
            ...completionPatch,
          };

          setOrganization(nextOrganization);
          setResult(null);

          // 3. Trigger completion callback
          if (typeof onCompleted === 'function') {
            await onCompleted(nextOrganization);
          }
        } catch (error) {
          console.error('[Onboarding Auto-Run] Failed:', error);
          // Permitir reintento si el componente sigue montado.
          autoRunStartedRef.current = false;
          if (isMountedRef.current) {
            setSaveError(error?.message || 'No pudimos completar la configuración automática.');
          }
        } finally {
          if (isMountedRef.current) {
            setSaving(false);
            setEntering(false);
          }
        }
      };

      autoRun();
    }

    return undefined;
  }, [loading, fatalError, organizationId, organization, userId, onCompleted]);

  const previewSummary = useMemo(
    () => ({
      created: [],
      updated: [
        'Configuracion inicial de impresion',
        `Tienda online en modo ${selectedPreset.shortLabel}`,
        'Perfil operativo del negocio',
      ],
    }),
    [selectedPreset.shortLabel],
  );

  const stepLabels = useMemo(() => {
    if (organization?.business_type) {
      return ['Base operativa'];
    }
    return ['Tipo de negocio', 'Base operativa'];
  }, [organization?.business_type]);

  const normalizedOnboardingStatus = String(organization?.onboarding_status || 'completed').toLowerCase();
  const isConfigured = normalizedOnboardingStatus === 'configured';
  const hasPreparedOnboarding = Boolean(result) || isConfigured;
  const shouldRenderWizard = shouldShowFirstRunOnboarding(organization);
  const shouldRedirectAway = !loading && !fatalError && !shouldRenderWizard;

  useEffect(() => {
    if (!organizationId || !shouldRedirectAway || typeof onUnavailable !== 'function') return;
    onUnavailable(organization);
  }, [organizationId, shouldRedirectAway, onUnavailable, organization]);

  if (!organizationId) {
    return null;
  }

  if (organization?.business_type && !organization?.onboarding_completed_at) {
    return (
      <div className="min-h-screen bg-[var(--gst-cream-50)] flex items-center justify-center px-4 py-8">
        <div className="flex flex-col items-center gap-6 rounded-[32px] border border-white/70 bg-[var(--surface)]/90 px-10 py-12 shadow-[0_30px_80px_rgba(15,23,42,0.12)] backdrop-blur-sm max-w-md text-center">
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-[var(--gst-mint-200)] blur-xl animate-pulse" />
            <div className="relative rounded-full border border-white bg-white/80 p-5 shadow-md">
              <Loader2 className="animate-spin text-[var(--gst-mint-600)]" size={40} strokeWidth={2.5} />
            </div>
          </div>
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.26em] text-[var(--gst-mint-600)]">
              PREPARANDO TU ESPACIO
            </p>
            <h2 className="mt-3 text-2xl font-black text-[var(--gst-graphite-900)] leading-tight">
              Configurando {organization.name || 'tu negocio'}
            </h2>
            <p className="mt-2 text-sm text-[var(--gst-graphite-700)] leading-relaxed">
              Sincronizando el catálogo y los módulos de <strong>{selectedPreset.label.toLowerCase()}</strong> para que empieces a vender hoy mismo.
            </p>
          </div>
          {saveError ? (
            <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700">
              {saveError}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (shouldRedirectAway) {
    return (
      <div className="min-h-screen bg-[var(--gst-cream-50)]">
        <div className="relative flex min-h-screen items-center justify-center px-4 py-8">
          <div className="flex flex-col items-center gap-4 rounded-[32px] border border-white/70 bg-[var(--surface)]/90 px-8 py-10 shadow-[0_30px_80px_rgba(15,23,42,0.12)] backdrop-blur-sm">
            <Loader2 className="animate-spin text-[var(--gst-graphite-700)]" size={30} />
            <div className="text-center">
              <p className="text-[11px] font-black uppercase tracking-[0.26em] text-[var(--gst-graphite-700)]">
                Redirigiendo
              </p>
              <p className="mt-2 text-sm text-[var(--gst-graphite-700)]">
                Estamos llevandote a la pantalla principal.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleApply = async () => {
    if (!organizationId || saving) return;

    try {
      setSaving(true);
      setSaveError('');

      const nextResult = await completeFirstRunOnboarding({
        organizationId,
        actorUserId: userId,
        businessType: selectedPreset.key,
      });

      setResult(nextResult);
      setOrganization((current) => ({
        ...current,
        ...nextResult.organization,
      }));
    } catch (error) {
      console.error(error);
      setSaveError(error?.message || 'No pudimos completar la configuracion automatica.');
    } finally {
      setSaving(false);
    }
  };

  const handleEnterSystem = async () => {
    if (!organizationId || entering) return;

    try {
      setEntering(true);
      setSaveError('');

      const completionPatch = await finalizeFirstRunOnboarding({
        organizationId,
        actorUserId: userId,
      });

      const nextOrganization = {
        ...organization,
        ...completionPatch,
      };

      setOrganization(nextOrganization);
      setResult(null);

      if (typeof onCompleted === 'function') {
        await onCompleted(nextOrganization);
      }
    } catch (error) {
      console.error(error);
      setSaveError(error?.message || 'No pudimos finalizar el onboarding.');
    } finally {
      setEntering(false);
    }
  };
  const renderStepIndicator = () => (
    // Tailwind no genera clases dinámicas (md:grid-cols-${n} se purga en prod).
    // Usamos clases estáticas seguras: si hay 1 paso, 1 columna; si hay 2+, 2 columnas en md+.
    <div
      className={`grid grid-cols-1 gap-3 ${
        stepLabels.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1'
      }`}
    >
      {stepLabels.map((label, index) => {
        const isCurrent = index === step;
        const isDone = index < step || hasPreparedOnboarding;

        return (
          <div
            key={label}
            className={`rounded-[1rem] border px-4 py-3 transition-all ${
              isCurrent
                ? 'border-[var(--gst-mint-500)] bg-[var(--gst-mint-500)] text-white shadow-[var(--gst-shadow-sm)]'
                : isDone
                  ? 'border-[color:var(--gst-border)] bg-[var(--surface)] text-[var(--gst-graphite-700)]'
                  : 'border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] text-[var(--gst-graphite-700)]'
            }`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-md text-[11px] font-black ${
                  isDone
                    ? isCurrent
                      ? 'bg-[var(--surface)] text-[var(--gst-mint-700)]'
                      : 'bg-[var(--gst-graphite-900)] text-white'
                    : isCurrent
                      ? 'bg-[var(--surface)] text-[var(--gst-mint-700)]'
                      : 'bg-[var(--surface)] text-[var(--gst-graphite-700)] border border-[color:var(--gst-border)]'
                }`}
              >
                {isDone ? <CheckCircle2 size={16} /> : index + 1}
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] opacity-90">Paso {index + 1}</p>
                <p className="text-sm font-bold">{label}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderOperationSummary = ({ description, summary, showIcon = false }) => {
    const createdItems = Array.isArray(summary?.created) ? summary.created : [];
    const updatedItems = Array.isArray(summary?.updated) ? summary.updated : [];

    return (
      <>
        {showIcon ? (
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-md bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)]">
            <CheckCircle2 size={28} />
          </div>
        ) : null}

        <h2 className={`${showIcon ? 'mt-4 text-2xl' : 'mt-1.5 text-[1.65rem]'} font-black leading-tight text-[var(--gst-graphite-900)]`}>
          Todo listo.
        </h2>
        <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-[var(--gst-graphite-700)]">
          {description}
        </p>

        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-[1.25rem] border border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[var(--gst-graphite-700)]">Se creo</p>
            <div className="mt-4 space-y-2">
              {createdItems.length > 0 ? (
                createdItems.map((item) => (
                  <div key={item} className="rounded-md bg-[var(--surface)] px-4 py-3 text-sm font-semibold text-[var(--text-default)]">
                    {item}
                  </div>
                ))
              ) : (
                <div className="rounded-md bg-[var(--surface)] px-4 py-3 text-sm font-semibold text-[var(--text-default)]">
                  No hizo falta crear nuevos registros.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[1.25rem] border border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[var(--gst-graphite-700)]">Se ajusto</p>
            <div className="mt-4 space-y-2">
              {updatedItems.map((item) => (
                <div key={item} className="rounded-md bg-[var(--surface)] px-4 py-3 text-sm font-semibold text-[var(--text-default)]">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderPresetCards = () => (
    <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
      {BUSINESS_PRESET_LIST.map((preset) => {
        const Icon = PRESET_ICON_MAP[preset.key] || Store;
        const isSelected = preset.key === selectedPreset.key;

        return (
          <button
            key={preset.key}
            type="button"
            onClick={() => setSelectedPresetKey(preset.key)}
            className={`group relative flex h-full w-full flex-col overflow-hidden rounded-[1.5rem] border text-left transition-all duration-200 ${
              isSelected
                ? 'border-[var(--gst-mint-500)] bg-[var(--surface)] shadow-[var(--gst-shadow-md)] -translate-y-0.5'
                : 'border-[color:var(--gst-border)] bg-[var(--surface)] shadow-[var(--gst-shadow-sm)] hover:-translate-y-0.5 hover:border-[var(--gst-mint-300)] hover:shadow-[var(--gst-shadow-md)]'
            }`}
          >
            <div className={`px-4 py-3 ${isSelected ? 'bg-[var(--gst-mint-500)] text-white' : 'bg-[var(--gst-cream-50)] text-[var(--gst-graphite-900)]'}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className={`h-11 w-11 rounded-md flex items-center justify-center ${isSelected ? 'bg-[var(--surface)] text-[var(--gst-mint-700)]' : 'border border-[color:var(--gst-border)] bg-[var(--surface)] text-[var(--gst-mint-700)]'}`}>
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className={`truncate text-[11px] font-black uppercase tracking-[0.24em] ${isSelected ? 'text-white/75' : 'text-[var(--gst-graphite-700)]'}`}>
                      {preset.shortLabel}
                    </p>
                    <h3 className={`truncate text-base font-black ${isSelected ? 'text-white' : 'text-[var(--gst-graphite-900)]'}`}>{preset.label}</h3>
                  </div>
                </div>
                <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.2em] ${isSelected ? 'border-white/30 bg-[var(--surface)] text-[var(--gst-mint-700)]' : 'border-[color:var(--gst-border)] bg-[var(--surface)] text-[var(--gst-graphite-700)]'}`}>
                  {isSelected ? 'Elegido' : 'Preset'}
                </span>
              </div>
            </div>
            <div className="flex flex-1 flex-col bg-[var(--surface)] p-4">
              <p className="text-[13px] leading-relaxed text-[var(--gst-graphite-700)]">
                {preset.description}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(preset.cardTags || buildFeatureLabel(preset.flags)).map((item) => (
                  <span
                    key={item}
                    className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.18em] ${
                      isSelected
                        ? 'bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)] border border-[var(--gst-mint-200)]'
                        : 'bg-[var(--gst-cream-50)] text-[var(--gst-graphite-700)] border border-[color:var(--gst-border)]'
                    }`}
                  >
                    {item}
                  </span>
                ))}
              </div>
              <div className={`mt-auto rounded-md border px-3 py-3 transition-colors ${
                isSelected
                  ? 'border-[var(--gst-mint-500)] bg-[var(--gst-mint-500)] text-white'
                  : 'border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] text-[var(--gst-graphite-700)]'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <p className={`text-[12px] font-black uppercase tracking-[0.16em] ${
                    isSelected ? 'text-white' : 'text-[var(--gst-graphite-700)]'
                  }`}>
                    {preset.cardCta || `Iniciar con ${preset.shortLabel.toLowerCase()}`}
                  </p>
                  <div className={`h-8 w-8 rounded-md flex items-center justify-center ${isSelected ? 'bg-[var(--surface)] text-[var(--gst-mint-700)]' : 'bg-[var(--surface)] text-[var(--gst-graphite-700)] border border-[color:var(--gst-border)] group-hover:text-[var(--gst-mint-700)]'}`}>
                    <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-[var(--gst-cream-50)]">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-[rgba(16,185,129,0.14)] blur-3xl" />
        <div className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-[rgba(254,215,170,0.24)] blur-3xl" />
        <div className="absolute bottom-0 left-16 h-64 w-64 rounded-full bg-[rgba(167,243,208,0.28)] blur-3xl" />
      </div>

      <div className="relative min-h-screen px-2 py-2 md:px-3 md:py-3 lg:px-4 lg:py-4">
        <div className="mx-auto w-full max-w-[1720px] overflow-hidden rounded-[28px] border border-[color:var(--gst-border)] bg-[rgba(255,255,255,0.76)] shadow-[var(--gst-shadow-lg)] xl:h-[calc(100dvh-2rem)]">
          <div className="h-full p-4 md:p-5 lg:p-6 xl:p-7 flex flex-col min-h-0">
            <div>
              <div className="mb-4">
                <GastifyBrand iconSize={40} subtitle="Configuración inicial" />
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[var(--gst-mint-200)] bg-[var(--gst-mint-100)] px-3.5 py-2 text-[9px] font-black uppercase tracking-[0.26em] text-[var(--gst-mint-700)]">
                <span className="w-2.5 h-2.5 rounded-full bg-[var(--gst-mint-500)]" />
                Primer ingreso a Gastify
              </div>

              <h1 className="mt-3 max-w-4xl text-[1.9rem] font-black tracking-tight text-[var(--gst-graphite-900)] md:text-[2.25rem] leading-tight">
                {organization?.business_type
                  ? `Configurando ${organization.name || 'tu negocio'}`
                  : `Vamos a dejar ${organization?.name || 'tu negocio'} listo para operar.`}
              </h1>
              <p className="mt-2 max-w-4xl text-[14px] leading-relaxed text-[var(--gst-graphite-700)]">
                {organization?.business_type
                  ? `Sembramos la configuración mínima para el rubro ${selectedPreset.label} para que arranques sin fricción.`
                  : `Elegí el tipo de negocio, te mostramos las funciones base y sembramos la configuración mínima para que arranques sin fricción.`}
              </p>
            </div>

            <div className="mt-4 shrink-0">
              {renderStepIndicator()}
            </div>

            {fatalError ? (
              <div className="mt-4 rounded-[28px] border border-rose-200 bg-rose-50 p-5 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
                <p className="text-sm font-black uppercase tracking-[0.22em] text-rose-600">No disponible</p>
                <p className="mt-2 text-base text-rose-700">{fatalError}</p>
              </div>
            ) : loading ? (
              <div className="mt-4 flex min-h-[280px] flex-1 items-center justify-center rounded-[32px] border border-[color:var(--border)] bg-[var(--surface)]">
                <div className="text-center">
                  <Loader2 className="mx-auto animate-spin text-[var(--gst-graphite-700)]" size={28} />
                  <p className="mt-4 text-[11px] font-black uppercase tracking-[0.26em] text-[var(--gst-graphite-700)]">
                    Preparando onboarding
                  </p>
                </div>
              </div>
            ) : hasPreparedOnboarding ? (
              <div className="mt-4 rounded-[32px] border border-[var(--gst-mint-200)] bg-[var(--surface)] p-5 md:p-6 shadow-sm lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
                {renderOperationSummary({
                  description: `Aplicamos el onboarding para ${(result?.preset?.label || selectedPreset.label).toLowerCase()} y ya dejamos creada la base minima para que empieces a operar.`,
                  summary: result?.summary || previewSummary,
                  showIcon: true,
                })}

                {saveError ? (
                  <div className="mt-5 rounded-md border border-rose-200 bg-rose-50 px-4 py-4 text-sm font-semibold text-rose-700">
                    {saveError}
                  </div>
                ) : null}

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={handleEnterSystem}
                    disabled={entering}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--gst-mint-500)] px-6 py-4 text-[11px] font-black uppercase tracking-[0.24em] text-[var(--text-default)] transition hover:bg-[var(--gst-mint-600)] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {entering ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Ingresando...
                      </>
                    ) : (
                      <>
                        Entrar a Gastify
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : !isAdminUser ? (
              <div className="mt-4 rounded-[32px] border border-[color:var(--border)] bg-[var(--surface)] p-6 shadow-sm lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
                <p className="text-[10px] font-black uppercase tracking-[0.26em] text-[var(--gst-graphite-700)]">Configuracion pendiente</p>
                <h2 className="mt-3 text-2xl font-black text-[var(--gst-graphite-900)]">Hace falta un administrador para completar este paso.</h2>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-[var(--gst-graphite-700)]">
                  La empresa todavia no termino su configuracion inicial. Cuando ingrese un usuario administrador va a poder elegir el tipo de negocio y dejar todo listo para el equipo.
                </p>
              </div>
            ) : (
              <div className="mt-4 rounded-[32px] border border-[color:var(--border)] bg-[var(--surface)] p-5 md:p-6 shadow-sm lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
                {step === 0 && !organization?.business_type ? (
                  <>
                    <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[var(--gst-graphite-700)]">Paso 1</p>
                    <h2 className="mt-1.5 text-[1.65rem] font-black text-[var(--gst-graphite-900)] leading-tight">¿Qué tipo de negocio va a operar con Gastify?</h2>
                    <p className="mt-2 max-w-4xl text-[14px] leading-relaxed text-[var(--gst-graphite-700)]">
                      Esto nos permite encender configuraciones útiles desde el arranque, como variantes para indumentaria o una tienda en modo gastronomía.
                    </p>

                    <div className="mt-5">
                      {renderPresetCards()}
                    </div>
                  </>
                ) : null}

                {(step === 1 || (step === 0 && organization?.business_type)) ? (
                  <>
                    <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[var(--gst-graphite-700)]">
                      {organization?.business_type ? 'Paso 1' : 'Paso 2'}
                    </p>
                    {renderOperationSummary({
                      description: `Aplicamos el onboarding para ${selectedPreset.label.toLowerCase()} y ya dejamos creada la base mínima para que empieces a operar.`,
                      summary: previewSummary,
                    })}

                    {saveError ? (
                      <div className="mt-5 rounded-md border border-rose-200 bg-rose-50 px-4 py-4 text-sm font-semibold text-rose-700">
                        {saveError}
                      </div>
                    ) : null}
                  </>
                ) : null}

                <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs uppercase tracking-[0.22em] text-[var(--gst-graphite-700)]">
                    Perfil elegido: <span className="font-black text-[var(--text-default)]">{selectedPreset.shortLabel}</span>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    {step > 0 && !organization?.business_type ? (
                      <button
                        type="button"
                        onClick={() => setStep((current) => Math.max(0, current - 1))}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-[color:var(--border)] bg-[var(--surface)] px-5 py-4 text-[11px] font-black uppercase tracking-[0.24em] text-[var(--gst-graphite-700)] transition hover:border-[color:var(--border)]"
                      >
                        <ChevronLeft size={16} />
                        Volver
                      </button>
                    ) : null}

                    {step < stepLabels.length - 1 ? (
                      <button
                        type="button"
                        onClick={() => setStep((current) => Math.min(stepLabels.length - 1, current + 1))}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--gst-mint-500)] px-6 py-4 text-[11px] font-black uppercase tracking-[0.24em] text-[var(--text-default)] transition hover:bg-[var(--gst-mint-600)]"
                      >
                        Continuar
                        <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleApply}
                        disabled={saving}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--gst-mint-500)] px-6 py-4 text-[11px] font-black uppercase tracking-[0.24em] text-[var(--text-default)] transition hover:bg-[var(--gst-mint-600)] disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                        {saving ? 'Configurando...' : 'Crear y empezar a vender'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
